import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, DropdownItemProps, DropdownProps, Grid } from 'semantic-ui-react';
import styled from 'styled-components';
import { FormikProps } from 'formik';

import { FieldLabel } from '../../../../../styles';
import { listProperties, PropertyWalletItemDTO } from '../../../../../services/properties';
import { maskZipCode } from '../../../../../services/masks';
import { CreateClaimEstateInitialInitialValuesType } from '../Formik/types/CreateClaimEstateInitialValuesTypes';

type Props = Pick<FormikProps<CreateClaimEstateInitialInitialValuesType>, 'setFieldValue' | 'values'> & {
  estateId?: string;
};

const getAddressLabel = (item: PropertyWalletItemDTO): string => {
  if (item?.address?.formatted) return item.address.formatted;

  return [
    item?.address?.street,
    item?.address?.number,
    item?.address?.complement,
    item?.address?.neighborhood,
    item?.address?.city,
    item?.address?.state
  ]
    .filter(Boolean)
    .join(', ');
};

export const WalletTag = styled.span`
  display: inline-block;
  font-size: 0.85rem;
  color: #2185d0;
  margin-bottom: 0.5rem;
`;

const ClaimPropertyWalletSearchDropdown = ({
  estateId,
  setFieldValue,
  values
}: Props): ReactElement | null => {
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<PropertyWalletItemDTO[]>([]);
  const requestIdRef = useRef(0);

  const selectedPublicId = values?.claimEstate?.propertyPublicId;

  useEffect(() => {
    if (!estateId) {
      setItems([]);
      return;
    }

    const timeout = setTimeout(async () => {
      requestIdRef.current += 1;
      const currentRequestId = requestIdRef.current;
      setIsLoading(true);

      try {
        const response = await listProperties({
          estateId,
          search: search.trim() || undefined,
          page: 1,
          limit: 10
        });

        if (currentRequestId === requestIdRef.current) {
          setItems(response.items || []);
        }
      } catch (_error) {
        if (currentRequestId === requestIdRef.current) {
          setItems([]);
        }
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [estateId, search]);

  const itemsByPublicId = useMemo(() => {
    const hash = new Map<string, PropertyWalletItemDTO>();
    items.forEach(item => {
      if (item?.publicId) hash.set(item.publicId, item);
    });
    return hash;
  }, [items]);

  const options: DropdownItemProps[] = items.map(item => ({
    key: item.publicId,
    value: item.publicId,
    text: item.identification || 'Sem código do imóvel',
    description: getAddressLabel(item)
  }));

  return (
    <Grid.Row>
      <Grid.Column width={16}>
        <FieldLabel>PESQUISAR NA CARTEIRA DE IMÓVEIS</FieldLabel>
        <Dropdown
          fluid
          selection
          clearable
          search
          loading={isLoading}
          options={options}
          value={selectedPublicId || ''}
          noResultsMessage="Nenhum imóvel encontrado."
          placeholder="Busque por CEP, bairro, logradouro ou código do imóvel"
          onSearchChange={(_event, data: DropdownProps) => {
            setSearch(String(data.searchQuery || ''));
          }}
          onChange={(_event, data: DropdownProps) => {
            const selectedId = String(data.value || '');

            if (!selectedId) {
              setFieldValue('claimEstate.propertyPublicId', undefined);
              setFieldValue('claimProperty.propertyCode', '');
              return;
            }

            const selected = itemsByPublicId.get(selectedId);
            if (!selected) return;

            setFieldValue('claimEstate.propertyPublicId', selected.publicId);
            setFieldValue('claimProperty.propertyCode', selected.identification || '');
            setFieldValue('claimProperty.zipCode', maskZipCode(selected?.address?.zipcode || ''));
            setFieldValue('claimProperty.street', selected?.address?.street || '');
            setFieldValue('claimProperty.district', selected?.address?.neighborhood || '');
            setFieldValue('claimProperty.city', selected?.address?.city || '');
            setFieldValue('claimProperty.state', selected?.address?.state || '');
            setFieldValue('claimProperty.number', selected?.address?.number || '');
            setFieldValue('claimProperty.complement', selected?.address?.complement || '');
            setFieldValue('claimProperty.country', 'BR');
          }}
        />
      </Grid.Column>
    </Grid.Row>
  );
};

export default ClaimPropertyWalletSearchDropdown;
